import { ContentType } from '@/lib/types/content'
import { recentlyViewedItemSchema } from '@/lib/validation/store/shared'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'

// Define the interface for items that can be added to recently viewed
interface RecentlyViewedItemInput {
  id: string
  path?: string
  href?: string
  title?: string
  icon?: string
  color?: string
  viewCount?: number
  createdAt?: string
  media?: any[]
  category?: any
  updatedAt?: string
  type: ContentType
  slug?: string
  details?: {
    address?: string
    city?: string
  }
}

interface RecentlyViewedItem {
  id: string
  path: string
  href: string
  title?: string
  icon?: string
  color?: string
  viewCount?: number
  createdAt?: string
  media?: any[]
  category?: any
  updatedAt: string
  type: ContentType
  slug?: string
  details?: {
    address?: string
    city?: string
  }
}

interface RecentlyViewedState {
  items: RecentlyViewedItem[]
  maxItems: number
}

const initialState: RecentlyViewedState = {
  items: [],
  maxItems: 5
}

const generatePath = (payload: RecentlyViewedItemInput): string => {
  if (payload.path) {
    return payload.path.replace(/^\/+|\/+$/g, '').toLowerCase()
  }

  if (payload.type && payload.slug) {
    return `${payload.type.toLowerCase()}s/${payload.slug}`.replace(/^\/+|\/+$/g, '').toLowerCase()
  }

  if (payload.slug) {
    return payload.slug.replace(/^\/+|\/+$/g, '').toLowerCase()
  }

  throw new Error('Item must have either path or slug')
}

const recentlyViewedSlice = createSlice({
  name: 'recentlyViewed',
  initialState,
  reducers: {
    addRecentItem: (state, action: PayloadAction<RecentlyViewedItemInput>) => {
      try {
        const cleanPath = generatePath(action.payload)

        const existingIndex = state.items.findIndex((item) => item.path.toLowerCase() === cleanPath.toLowerCase())

        const now = new Date().toISOString()
        const newItem: RecentlyViewedItem = {
          ...action.payload,
          id: action.payload.id || cleanPath,
          path: cleanPath,
          href: action.payload.href || `/${cleanPath}`,
          updatedAt: now,
          createdAt: action.payload.createdAt || now,
          slug: action.payload.slug || undefined,
          type: (action.payload.type as ContentType) || ContentType.NONE
        }

        const validationResult = recentlyViewedItemSchema.safeParse(newItem)
        if (!validationResult.success) {
          console.error('Invalid recently viewed item:', validationResult.error)
          return
        }

        if (existingIndex !== -1) {
          state.items.splice(existingIndex, 1)
          state.items.unshift({
            ...validationResult.data
          })
        } else {
          state.items.unshift(validationResult.data)
          if (state.items.length > state.maxItems) {
            state.items.pop()
          }
        }
      } catch (error) {
        console.error('Failed to add recent item:', error)
      }
    },
    removeRecentItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload)
    },
    clearRecentItems: (state) => {
      state.items = []
    },
    setMaxItems: (state, action: PayloadAction<number>) => {
      state.maxItems = action.payload
      if (state.items.length > state.maxItems) {
        state.items = state.items.slice(0, state.maxItems)
      }
    }
  }
})

// Selectors
export const selectRecentlyViewed = (state: RootState) => state.recentlyViewed.items
export const selectMaxItems = (state: RootState) => state.recentlyViewed.maxItems

// Export types for use in other components
export type { RecentlyViewedItem, RecentlyViewedItemInput }

export const { addRecentItem, removeRecentItem, clearRecentItems, setMaxItems } = recentlyViewedSlice.actions
export default recentlyViewedSlice.reducer
